import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { Button, ToastNotification } from '@carbon/react';
import { MapBoundary } from '@carbon/react/icons';
import type { ColumnFiltersState } from '@tanstack/react-table';
import { DataTable } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import { toastAtom } from './atoms';
import styles from './type-curves-listing.module.scss';
import { TypeCurvesContentSwitcher } from './type-curves-content-switcher';
import { TypeCurvesListingFilters } from './type-curves-listing-filters';
import { buildApiFilters } from './utils';
import { ListingMap, type ListingBbox } from './listing-map';
import type { TcMapView } from './use-tc-map-source';
import { typeCurvesListingColumns } from './type-curves-listing-columns';
import { typeCurveWorkspacesColumns } from './type-curve-workspaces-columns';
import { typeCurveScenariosCatalogColumns } from './type-curve-scenarios-catalog-columns';
import {
  INVENTORY_COLUMNS_HIDDEN_BY_DEFAULT,
  inventoryWorkspacesColumns,
  type InventoryWorkspaceTableRow,
} from './inventory-workspaces-columns';
import {
  useTypeCurvesListing,
  DEFAULT_PAGE_SIZE,
  type TypeCurvesListingFilters as ApiFilters,
  type TypeCurvesListingFacets,
} from './use-type-curves-listing';
import { useTypeCurveWorkspacesListing } from './use-type-curve-workspaces-listing';
import { useTypeCurveScenariosListing } from './use-type-curve-scenarios-listing';
import { useInventoryWorkspacesListing } from './use-inventory-workspaces-listing';
import type {
  TypeCurveListingRow,
  TypeCurveWorkspaceListQueryResponse,
} from 'types/api-responses';
import type { TypeCurveScenarioRow } from 'pages/type-curve-scenarios-list/types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Union of `[minLng, minLat, maxLng, maxLat]` extents; null when none valid.
function unionBbox(
  boxes: ReadonlyArray<ListingBbox | null | undefined>
): ListingBbox | null {
  const valid = boxes.filter(
    (b): b is ListingBbox =>
      Array.isArray(b) && b.length === 4 && b.every(Number.isFinite)
  );
  if (!valid.length) return null;
  return valid.reduce<ListingBbox>(
    (acc, b) => [
      Math.min(acc[0], b[0]),
      Math.min(acc[1], b[1]),
      Math.max(acc[2], b[2]),
      Math.max(acc[3], b[3]),
    ],
    [Infinity, Infinity, -Infinity, -Infinity]
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface TypeCurvesContentProps {
  listTab: number;
  onListTabChange: (tab: number) => void;
  onTcListingStateChange?: (state: {
    queryParams: ApiFilters;
    isFetching: boolean;
    totalCount: number | undefined;
  }) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const TypeCurvesContent: FC<TypeCurvesContentProps> = ({
  listTab,
  onListTabChange,
  onTcListingStateChange,
}) => {
  const [toast, setToast] = useAtom(toastAtom);

  // Shared filter state: columnFilters (for seeding each DataTable) + API shape
  const [sharedColumnFilters, setSharedColumnFilters] =
    useState<ColumnFiltersState>([]);

  // Seed through buildApiFilters so defaults (e.g. active: true) are present on
  // the first request, not only after the user touches a filter.
  const [apiFilters, setApiFilters] = useState<ApiFilters>(() =>
    buildApiFilters([])
  );

  const [showMap, setShowMap] = useState(true);

  // Selected row drives the camera. The id (not the bbox) is the fit trigger —
  // see ListingMap.
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [selectedBbox, setSelectedBbox] = useState<ListingBbox | null>(null);
  /**
   * Selected row's Type Curve Area id — the highlight key for the Type Curves /
   * Workspaces map layer (features are keyed by area `id`). Null for scenario
   * rows, which highlight by scenario id instead.
   */
  const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);

  // Inventory Workspaces highlight a Type Curve Area, the same as the
  // Workspaces view, so they share its tiles and highlight path.
  let mapView: TcMapView = 'type_curves';
  if (listTab === 1) mapView = 'workspaces';
  // The inventories tile resolves the zone-grained query, so it draws every
  // area the filtered workspaces' zones cover rather than the workspaces view's.
  if (listTab === 3) mapView = 'inventories';
  if (listTab === 2) mapView = 'scenarios';

  // React Query — only active tab fires
  const tcListing = useTypeCurvesListing(apiFilters, {
    enabled: listTab === 0,
  });
  const wsListing = useTypeCurveWorkspacesListing(apiFilters, {
    enabled: listTab === 1,
  });
  const scListing = useTypeCurveScenariosListing(apiFilters, {
    enabled: listTab === 2,
  });
  const invListing = useInventoryWorkspacesListing(apiFilters, {
    enabled: listTab === 3,
  });
  // Already one row per (area, formation, type curve) zone — the server
  // flattens to the grain the listing renders.
  const invRows = invListing.rows as InventoryWorkspaceTableRow[];

  let activeFacets: TypeCurvesListingFacets | undefined = tcListing.facets;
  if (listTab === 1) activeFacets = wsListing.facets;
  if (listTab === 2) activeFacets = scListing.facets;
  if (listTab === 3) activeFacets = invListing.facets;

  // Notify parent of the ACTIVE listing's state for the Export dropdown. The count, the search text
  // and the sort follow the tab: the Inventories export is filtered by the same panel but counts (and
  // searches) its own workspaces, and reporting the Type Curves total would disable the dropdown
  // whenever that view happened to be empty. The search and the sort live inside each listing hook
  // (NOT in apiFilters), so spread the active tab's `requestParams` — the same encoding its listing
  // query posts — into queryParams, so an export is scoped and ordered by the active tab's own listing
  // query. Until the Type Curves listing's first response lands, publish an UNDEFINED count so the
  // dropdown shows "Loading data…" rather than a premature "no type curves match" (count 0 before the
  // data arrives). `isFetching` rides along so the Excel export can hold, with a loading label, while
  // the listing fetches.
  //
  // The effect depends on stable members only (the memoized `requestParams`, booleans, numbers): the
  // hook's return object is new on every render, so depending on it would re-fire the effect, which
  // sets parent state, which re-renders this component — a loop.
  const active = listTab === 3 ? invListing : tcListing;
  const activeRequestParams = active.requestParams;
  const activeIsFetching = active.isFetching;
  useEffect(() => {
    const totalCount =
      listTab === 3
        ? invListing.totalCount
        : tcListing.hasLoaded
          ? tcListing.totalCount
          : undefined;
    onTcListingStateChange?.({
      queryParams: { ...apiFilters, ...activeRequestParams },
      totalCount,
      isFetching: activeIsFetching,
    });
  }, [
    apiFilters,
    listTab,
    activeRequestParams,
    activeIsFetching,
    invListing.totalCount,
    tcListing.hasLoaded,
    tcListing.totalCount,
    onTcListingStateChange,
  ]);

  // When filter changes in any DataTable, sync shared state
  const handleFilterChange = useCallback((state: TableState) => {
    setSharedColumnFilters(state.columnFilters);
    setApiFilters(buildApiFilters(state.columnFilters));
  }, []);

  // Selection is per-table, so it is dropped on a tab switch — otherwise a
  // Workspaces bbox / highlight would leak into the Scenarios map.
  const handleTabChange = useCallback(
    (tab: number) => {
      setSelectedRowId(null);
      setSelectedBbox(null);
      setSelectedAreaId(null);
      onListTabChange(tab);
    },
    [onListTabChange]
  );

  // TC handlers
  const handleTcPagination = useCallback(
    (state: TableState) => tcListing.onPaginationChange(state),
    [tcListing]
  );
  const handleTcSort = useCallback(
    (state: TableState) => tcListing.onSortChange(state),
    [tcListing]
  );
  const handleTcSearch = useCallback(
    (state: TableState) => tcListing.onGlobalSearchChange(state),
    [tcListing]
  );

  // WS handlers
  const handleWsPagination = useCallback(
    (state: TableState) => wsListing.onPaginationChange(state),
    [wsListing]
  );
  const handleWsSort = useCallback(
    (state: TableState) => wsListing.onSortChange(state),
    [wsListing]
  );
  const handleWsSearch = useCallback(
    (state: TableState) => wsListing.onGlobalSearchChange(state),
    [wsListing]
  );

  // SC handlers
  const handleScPagination = useCallback(
    (state: TableState) => scListing.onPaginationChange(state),
    [scListing]
  );
  const handleScSort = useCallback(
    (state: TableState) => scListing.onSortChange(state),
    [scListing]
  );
  const handleScSearch = useCallback(
    (state: TableState) => scListing.onGlobalSearchChange(state),
    [scListing]
  );

  /**
   * Row click → fit the camera to that row and highlight it on the map.
   *
   * `onSelectionChange` fires on row click whether or not `selectionType` is
   * configured (see the no-selectionType branch in data-table.tsx), so no config
   * change is needed. Every row carries `bbox` as `[minLng, minLat, maxLng,
   * maxLat]`, or null when it has no geometry.
   *
   * The highlight key differs by view: Type Curves / Workspaces highlight by the
   * row's `typeCurveAreaId` (the area layer is keyed by area `id`); Scenarios
   * highlight by the scenario id (derived from the selected row id).
   */
  const selectRow = useCallback(
    (
      state: TableState,
      rows: ReadonlyArray<{
        id: number;
        bbox?: ListingBbox | null;
        typeCurveAreaId?: number | null;
      }>
    ) => {
      const id = state.selection.toggledNodes[0];
      if (!id) {
        setSelectedRowId(null);
        setSelectedBbox(null);
        setSelectedAreaId(null);
        return;
      }
      const row = rows.find((r) => String(r.id) === id);
      setSelectedRowId(id);
      setSelectedBbox(row?.bbox ?? null);
      setSelectedAreaId(row?.typeCurveAreaId ?? null);
    },
    []
  );

  const handleTcSelection = useCallback(
    (state: TableState) => selectRow(state, tcListing.rows),
    [selectRow, tcListing.rows]
  );
  // Inventory Workspaces handlers
  const handleInvPagination = useCallback(
    (state: TableState) => invListing.onPaginationChange(state),
    [invListing]
  );
  const handleInvSort = useCallback(
    (state: TableState) => invListing.onSortChange(state),
    [invListing]
  );
  const handleInvSearch = useCallback(
    (state: TableState) => invListing.onGlobalSearchChange(state),
    [invListing]
  );

  const handleInvSelection = useCallback(
    (state: TableState) => {
      const id = state.selection.toggledNodes[0];
      if (!id) {
        setSelectedRowId(null);
        setSelectedBbox(null);
        setSelectedAreaId(null);
        return;
      }
      // Framing and highlighting ride on the row now: `bbox` is the union of
      // the workspace's areas and `typeCurveAreaIds` names them, so neither
      // needs the geometry round-trip the other tabs do. Selection keys on
      // rowId, so the id lookup matches the zone that was clicked.
      const row = invRows.find((r) => r.rowId === id);
      setSelectedRowId(id);
      setSelectedAreaId(row?.typeCurveAreaId ?? null);
      setSelectedBbox((row?.bbox as ListingBbox | undefined) ?? null);
    },
    [invRows]
  );

  const handleWsSelection = useCallback(
    (state: TableState) => selectRow(state, wsListing.rows),
    [selectRow, wsListing.rows]
  );
  const handleScSelection = useCallback(
    (state: TableState) => selectRow(state, scListing.rows),
    [selectRow, scListing.rows]
  );

  // The rows the map draws for the active view: Type Curves / Workspaces map to
  // their Type Curve Area, Scenarios to themselves.
  const mapRows =
    mapView === 'workspaces'
      ? wsListing.rows
      : mapView === 'scenarios'
        ? scListing.rows
        : mapView === 'inventories'
          ? invListing.rows
          : tcListing.rows;

  // Global search text of the active view. Search lives inside each listing
  // hook, so read the active one to fold it into the map's filter payload.
  const activeSearch =
    listTab === 1
      ? wsListing.search
      : listTab === 2
        ? scListing.search
        : listTab === 3
          ? invListing.search
          : tcListing.search;

  // The filter payload the map sends the tile: the shared panel filters plus the
  // active search. The tile resolves this to the FULL matching set, so the map
  // draws every match while the table paginates — the two cannot drift because
  // both derive from the same filters.
  const mapFilters = useMemo<ApiFilters>(
    () => ({ ...apiFilters, text: activeSearch || undefined }),
    [apiFilters, activeSearch]
  );

  // Union extent of the page, framed on landing when no row is selected.
  const pageBbox = useMemo(
    () =>
      unionBbox(
        (mapRows as ReadonlyArray<{ bbox?: ListingBbox | null }>).map(
          (r) => r.bbox
        )
      ),
    [mapRows]
  );

  // On the Scenarios view the selected row id IS the scenario id the tile tags
  // its features with, so derive the highlight from it rather than tracking new
  // state.
  const selectedScenarioId =
    mapView === 'scenarios' && selectedRowId ? Number(selectedRowId) : null;

  // Map-feature click → owning row. Applies the same selection a row click would,
  // so the camera fits and the shape highlights. Areas are shared, so this picks
  // the first row in the area (an accepted limitation of an overview map).
  const onSelectAreaId = useCallback(
    (areaId: number) => {
      const row = (
        mapRows as ReadonlyArray<{
          id: number;
          bbox?: ListingBbox | null;
          typeCurveAreaId?: number | null;
        }>
      ).find((r) => r.typeCurveAreaId === areaId);
      if (!row) return;
      const rowKey = (row as { rowId?: string }).rowId ?? String(row.id);
      setSelectedRowId(rowKey);
      setSelectedBbox(row.bbox ?? null);
      setSelectedAreaId(areaId);
    },
    [mapRows]
  );

  const onSelectScenarioId = useCallback(
    (scenarioId: number) => {
      const row = (
        mapRows as ReadonlyArray<{ id: number; bbox?: ListingBbox | null }>
      ).find((r) => r.id === scenarioId);
      setSelectedRowId(String(scenarioId));
      setSelectedBbox(row?.bbox ?? null);
      setSelectedAreaId(null);
    },
    [mapRows]
  );

  // Shared filter panel rendered inside each DataTable's renderFilterPanel.
  // Identical on every view — each table registers the columns its filters
  // bind to — except Type Curve ID, which only the Inventories view answers.
  const filterPanel = () => (
    <TypeCurvesListingFilters
      facets={activeFacets}
      showTypeCurveId={listTab === 3}
    />
  );

  // Common DataTable config — same across all 3 tabs
  const sharedTableConfig = {
    sorting: true,
    globalSearch: true,
    columnFilters: false,
    columnResizing: true,
    columnReordering: true,
    columnPinning: true,
    columnVisibility: true,
  };

  // Common initial table state fields — same across all 3 tabs
  const sharedInitialState = {
    pageSize: DEFAULT_PAGE_SIZE,
    columnFilters: sharedColumnFilters,
  };

  // Shared toolbar left actions
  const sharedToolbarActions = [
    <TypeCurvesContentSwitcher
      key="switcher"
      activeTab={listTab}
      onChange={handleTabChange}
    />,
    <Button
      key="map-toggle"
      kind="ghost"
      size="md"
      hasIconOnly
      renderIcon={MapBoundary}
      iconDescription={showMap ? 'Hide map' : 'Show map'}
      tooltipPosition="left"
      onClick={() => setShowMap((v) => !v)}
    />,
  ];

  // The map draws the full set matching the current filters (resolved by the
  // tile), so it shows every match while the table paginates the same set.
  const mapPanel = showMap ? (
    <ListingMap
      view={mapView}
      filters={mapFilters}
      // Inventories does not frame on landing: a workspace's bbox is the union
      // of every area it touches, so the union across the page is close to the
      // whole basin and the arrival zoom reads as random. Row clicks still fit.
      pageBbox={mapView === 'inventories' ? null : pageBbox}
      selectedRowId={selectedRowId}
      selectedBbox={selectedBbox}
      selectedAreaId={selectedAreaId}
      selectedScenarioId={selectedScenarioId}
      onSelectAreaId={onSelectAreaId}
      onSelectScenarioId={onSelectScenarioId}
    />
  ) : undefined;

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.pageLayout}>
        <div className={styles.mainContent}>
          {listTab === 0 && (
            <DataTable<TypeCurveListingRow & Record<string, unknown>>
              data={
                tcListing.rows as (TypeCurveListingRow &
                  Record<string, unknown>)[]
              }
              columns={typeCurvesListingColumns}
              getRowId={(row) => String(row.id)}
              persistenceKey="tc-listing-v3"
              isLoading={tcListing.isFetching}
              error={tcListing.error}
              initialTableState={{
                ...sharedInitialState,
                sortColumn: 'uniqueTcId',
                sortDirection: 'desc',
                columnOrder: [
                  'typeCurveScenario',
                  'uniqueTcId',
                  'verification',
                  'validation',
                  'lastVerifiedBy',
                  'typeCurveArea',
                  'basin',
                  'formation',
                  'status',
                  'promotedBy',
                  'state',
                  'wellCount',
                  'wellSpacingFt',
                  'oilEurMbbl30yr',
                  'gasEurMmcf30yr',
                  'waterEurMbbl30yr',
                  'sectionEurMboe20',
                  'lastVerificationDate',
                  'newWellsSinceCreatedCount',
                  'newWellsSinceLastQcCount',
                  'dataLoadSource',
                  'createdBy',
                  'createdAt',
                  'updatedAt',
                ],
              }}
              scroll={{
                mode: 'paginated',
                serverDriven: true,
                totalCount: tcListing.totalCount,
              }}
              onPaginationChange={handleTcPagination}
              onSortChange={handleTcSort}
              onGlobalSearchChange={handleTcSearch}
              onFilterChange={handleFilterChange}
              onSelectionChange={handleTcSelection}
              searchPlaceholder="Search type curves..."
              renderFilterPanel={filterPanel}
              leftToolbarActions={sharedToolbarActions}
              betweenToolbarAndContent={mapPanel}
              config={sharedTableConfig}
            />
          )}

          {listTab === 1 && (
            <DataTable<
              TypeCurveWorkspaceListQueryResponse & Record<string, unknown>
            >
              data={
                wsListing.rows as (TypeCurveWorkspaceListQueryResponse &
                  Record<string, unknown>)[]
              }
              columns={typeCurveWorkspacesColumns}
              getRowId={(row) => String(row.id)}
              persistenceKey="ws-listing-v3"
              isLoading={wsListing.isFetching}
              error={wsListing.error}
              initialTableState={{
                ...sharedInitialState,
                sortColumn: 'updatedAt',
                sortDirection: 'desc',
                columnOrder: [
                  'workspace',
                  'uniqueTcId',
                  'typeCurveScenario',
                  'createdBy',
                  'createdAt',
                  'updatedAt',
                  'tasks',
                  'promotedBy',
                  'verification',
                  'status',
                  'promotedAt',
                  'unitsVerified',
                ],
              }}
              scroll={{
                mode: 'paginated',
                serverDriven: true,
                totalCount: wsListing.totalCount,
              }}
              onPaginationChange={handleWsPagination}
              onSortChange={handleWsSort}
              onGlobalSearchChange={handleWsSearch}
              onFilterChange={handleFilterChange}
              onSelectionChange={handleWsSelection}
              searchPlaceholder="Search workspaces..."
              renderFilterPanel={filterPanel}
              leftToolbarActions={sharedToolbarActions}
              betweenToolbarAndContent={mapPanel}
              config={sharedTableConfig}
            />
          )}

          {listTab === 2 && (
            <DataTable<TypeCurveScenarioRow>
              data={scListing.rows}
              columns={typeCurveScenariosCatalogColumns}
              getRowId={(row) => String(row.id)}
              persistenceKey="sc-catalog-v1"
              isLoading={scListing.isFetching}
              error={scListing.error}
              initialTableState={{
                ...sharedInitialState,
                sortColumn: 'name',
                sortDirection: 'asc',
                columnOrder: [
                  'name',
                  'basin_area_count',
                  'development_type',
                  'dca_scenario_name',
                  'createdBy',
                  'createdAt',
                  'updatedAt',
                  'updated_by',
                ],
              }}
              scroll={{
                mode: 'paginated',
                serverDriven: true,
                totalCount: scListing.totalCount,
              }}
              onPaginationChange={handleScPagination}
              onSortChange={handleScSort}
              onGlobalSearchChange={handleScSearch}
              onFilterChange={handleFilterChange}
              onSelectionChange={handleScSelection}
              searchPlaceholder="Search by TC Scenario Name"
              renderFilterPanel={filterPanel}
              leftToolbarActions={sharedToolbarActions}
              betweenToolbarAndContent={mapPanel}
              config={{
                ...sharedTableConfig,
                export: {
                  exportPresets: [
                    {
                      name: 'Export',
                      format: 'xlsx',
                      filename: 'type_curve_scenarios',
                    },
                  ],
                },
              }}
            />
          )}
          {listTab === 3 && (
            <DataTable<InventoryWorkspaceTableRow>
              data={invRows}
              columns={inventoryWorkspacesColumns}
              // rowId is per LINE; `id` repeats down a workspace's zones.
              getRowId={(row) => row.rowId}
              persistenceKey="inventory-ws-listing-v5"
              isLoading={invListing.isFetching}
              error={invListing.error}
              initialTableState={{
                ...sharedInitialState,
                columnVisibility: INVENTORY_COLUMNS_HIDDEN_BY_DEFAULT,
              }}
              scroll={{
                mode: 'paginated',
                serverDriven: true,
                totalCount: invListing.totalCount,
              }}
              onPaginationChange={handleInvPagination}
              onSortChange={handleInvSort}
              onGlobalSearchChange={handleInvSearch}
              onFilterChange={handleFilterChange}
              onSelectionChange={handleInvSelection}
              searchPlaceholder="Search inventory workspaces..."
              renderFilterPanel={filterPanel}
              leftToolbarActions={sharedToolbarActions}
              betweenToolbarAndContent={mapPanel}
              config={sharedTableConfig}
            />
          )}
        </div>
      </div>

      {toast && (
        <ToastNotification
          kind={toast.kind}
          title={toast.title}
          subtitle={toast.subtitle}
          lowContrast
          timeout={toast.timeout || 5000}
          onClose={() => {
            setToast(null);
            return false;
          }}
          className={styles.toastNotification}
        />
      )}
    </div>
  );
};
